import { Controller } from "@hotwired/stimulus"

// Visible feedback for the browser's own required-field validation on the
// mobile shell (CONEK-8031). Mounted on the mobile layout's <body>, so it
// covers every mobile form.
//
// Ui::SelectComponent keeps the real <select required> hidden behind Tom
// Select. When the rep taps Save with it empty, the browser cancels the submit
// and fires `invalid` on that hidden control, but it has nothing visible to
// anchor its validation bubble to, so the WebView shows nothing: the tap just
// does nothing. This turns each `invalid` into an inline message next to the
// visible control and scrolls the first one of the attempt into view, then
// clears the message as soon as the field changes (the same idiom as
// contacts--form-errors).
//
// Ui::EntitySelectorComponent (Site, Contact…) is the other case: its value
// rides in an <input type="hidden" required>, and hidden inputs are barred
// from constraint validation, so the browser never fires `invalid` for it and
// lets the submit through to a server 422 (CONEK-8031 rework). Those are
// checked here: on `submit`, an empty one blocks the submit; when the browser
// has already blocked it for another field, they are marked alongside it so
// every missing field shows at once.
//
// `invalid` does not bubble, so it is listened for in the capture phase.
const MESSAGE = "data-mobile-form-validation-message"
const SELECT_HOST = '[data-controller~="ui--select"]'
const ENTITY_SELECTOR_HOST = '[data-controller~="ui--entity-selector"]'
let messageCount = 0

export default class extends Controller {
  static values = { requiredMessage: String }

  connect() {
    this.attempt = null
    this.onInvalid = this.onInvalid.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onEdit = this.onEdit.bind(this)
    this.clearAll = this.clearAll.bind(this)
    this.element.addEventListener("invalid", this.onInvalid, true)
    this.element.addEventListener("submit", this.onSubmit, true)
    this.element.addEventListener("input", this.onEdit, true)
    this.element.addEventListener("change", this.onEdit, true)
    // A page Turbo caches with a message showing must not come back with a
    // stale error.
    document.addEventListener("turbo:before-cache", this.clearAll)
  }

  disconnect() {
    this.element.removeEventListener("invalid", this.onInvalid, true)
    this.element.removeEventListener("submit", this.onSubmit, true)
    this.element.removeEventListener("input", this.onEdit, true)
    this.element.removeEventListener("change", this.onEdit, true)
    document.removeEventListener("turbo:before-cache", this.clearAll)
  }

  onInvalid(event) {
    // Suppress the browser's own bubble: once the field is in view, iOS
    // paints a truncated one next to the inline message. The submit stays
    // blocked; only the inline message is shown, on every platform.
    event.preventDefault()
    const control = event.target
    this.markInvalid(control)
    // The browser has blocked this submit already, so `submit` never fires:
    // mark the empty entity selectors of the same form now.
    if (control.form) this.markEmptyEntitySelectors(control.form)
  }

  onSubmit(event) {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return
    if (form.noValidate || event.submitter?.formNoValidate) return
    if (!this.markEmptyEntitySelectors(form)) return

    // Capture phase on the <body>: stopping here keeps the event from
    // reaching the form's own listeners (bridge--offline-queue would queue
    // it while offline) and Turbo's, so nothing is sent.
    event.preventDefault()
    event.stopPropagation()
  }

  // Marks every required entity selector in the form that has no value.
  // Returns whether any was empty.
  markEmptyEntitySelectors(form) {
    const empty = Array.from(
      form.querySelectorAll('input[type="hidden"][required]'),
    ).filter(
      (input) =>
        !input.disabled &&
        input.value.trim() === "" &&
        input.closest(ENTITY_SELECTOR_HOST),
    )
    empty.forEach((input) => this.markInvalid(input))
    return empty.length > 0
  }

  markInvalid(control) {
    const anchor = this.visibleAnchor(control)

    let message = this.messageFor(anchor)
    if (!message) {
      message = this.buildMessage()
      anchor.after(message)
    }
    // Mark what assistive tech actually lands on: the control itself and, for
    // a select or an entity selector, its visible combobox (the real value
    // carrier is hidden). aria-describedby is a token list Ui::* components
    // already use (hint or server error): add the message id, never replace.
    for (const target of this.invalidTargets(control, anchor)) {
      target.setAttribute("aria-invalid", "true")
      const ids = this.describedBy(target)
      if (!ids.includes(message.id)) ids.push(message.id)
      target.setAttribute("aria-describedby", ids.join(" "))
    }

    this.scrollToTopmost(anchor)
  }

  // One submit marks every bad field in a row; only the one highest on the
  // page scrolls into view. The attempt closes on the next task, so the next
  // submit scrolls again.
  scrollToTopmost(anchor) {
    if (!this.attempt) {
      const attempt = { anchors: [] }
      this.attempt = attempt
      setTimeout(() => {
        this.attempt = null
        const [first] = attempt.anchors.sort((a, b) =>
          a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
            ? -1
            : 1,
        )
        first?.scrollIntoView({ block: "center", behavior: "smooth" })
      }, 0)
    }
    if (!this.attempt.anchors.includes(anchor))
      this.attempt.anchors.push(anchor)
  }

  onEdit(event) {
    const control = event.target
    const anchor = this.visibleAnchor(control)
    // Behind a Tom Select or an entity selector only the hidden value carrier
    // changing counts; typing in their search boxes is not a fix yet.
    if (!this.widgetHost(control) || this.isValueCarrier(control)) {
      const message = this.messageFor(anchor)
      if (message) this.clearMessage(message)
    }
    // After the event has run its course: the Title is written by a
    // bubble-phase listener of this same `change`, after this capture one.
    setTimeout(() => this.clearResolved(), 0)
  }

  // A field can be filled without an event of its own: picking a task type
  // writes the Title from code. Any edit in the form also drops the messages
  // of fields that are valid by then.
  clearResolved() {
    this.element.querySelectorAll(`[${MESSAGE}]`).forEach((message) => {
      const control = this.element.querySelector(
        `:is(input, select, textarea):not([role="combobox"])[aria-describedby~="${message.id}"]`,
      )
      if (control && this.isFilled(control)) this.clearMessage(message)
    })
  }

  // `validity` rather than checkValidity(): the latter fires `invalid` again.
  isFilled(control) {
    return control.type === "hidden"
      ? control.value.trim() !== ""
      : control.validity.valid
  }

  clearAll() {
    this.element
      .querySelectorAll(`[${MESSAGE}]`)
      .forEach((message) => this.clearMessage(message))
  }

  // Un-marks every element the message describes, removing only its own id
  // from aria-describedby, then removes the message.
  clearMessage(message) {
    this.element
      .querySelectorAll(`[aria-describedby~="${message.id}"]`)
      .forEach((target) => {
        target.removeAttribute("aria-invalid")
        const ids = this.describedBy(target).filter((id) => id !== message.id)
        if (ids.length) target.setAttribute("aria-describedby", ids.join(" "))
        else target.removeAttribute("aria-describedby")
      })
    message.remove()
  }

  describedBy(element) {
    return (element.getAttribute("aria-describedby") || "")
      .split(/\s+/)
      .filter(Boolean)
  }

  invalidTargets(control, anchor) {
    const combobox = this.widgetHost(control)
      ? anchor.querySelector('[role="combobox"]')
      : null
    return combobox ? [control, combobox] : [control]
  }

  isValueCarrier(control) {
    return control.tagName === "SELECT" || control.type === "hidden"
  }

  // The element the rep actually sees, which the message goes under: Tom
  // Select's or the entity selector's host; for any other control, the inline
  // row it sits in, if any. Ui::InputComponent draws its bordered box as a
  // flex row around the <input>, so a message placed right after the input
  // landed inside the box, beside the text (CONEK-8031 QA).
  visibleAnchor(control) {
    return this.widgetHost(control) || this.inlineRow(control) || control
  }

  widgetHost(control) {
    return (
      control.closest?.(SELECT_HOST) || control.closest?.(ENTITY_SELECTOR_HOST)
    )
  }

  // Only a row that holds this one field (an icon beside it is fine): fields
  // laid out side by side in one row would otherwise share a single message.
  inlineRow(control) {
    const parent = control.parentElement
    if (!parent || parent === this.element || parent === control.form)
      return null
    const style = getComputedStyle(parent)
    const flex = style.display === "flex" || style.display === "inline-flex"
    if (!flex || style.flexDirection.startsWith("column")) return null
    const fields = parent.querySelectorAll(
      'input:not([type="hidden"]), select, textarea',
    )
    return fields.length === 1 ? parent : null
  }

  messageFor(anchor) {
    const next = anchor.nextElementSibling
    return next?.hasAttribute(MESSAGE) ? next : null
  }

  buildMessage() {
    const message = document.createElement("p")
    message.id = `mobile-form-validation-${++messageCount}`
    message.setAttribute(MESSAGE, "")
    message.setAttribute("role", "alert")
    message.className = "mt-1 text-xs text-danger"
    message.textContent = this.requiredMessageValue
    return message
  }
}
